import { toast } from "sonner";
import { ApiResponse } from "../types/responseType";
import customFetch from "../util/config/axiosConfig";
import {
  MarkAttendancePayload,
  MarkStudentAttendancePayload,
  StaffDayWiseAttendance,
  StaffMonthlyAttendance,
  StudentDayWiseAttendance,
  StudentMonthlyAttendance,
} from "../types/attendanceType";
import { getErrorMessage } from "../util/functions/UniversalFunctions";

const attendanceApis = {
  // mark staff
  markStaffAttendance: async (
    payload: MarkAttendancePayload,
    branchCode: string,
  ): Promise<ApiResponse<string> | undefined> => {
    try {
      const response = await customFetch.post<ApiResponse<string>>(
        `/api/v1/attendance/staff/mark`,
        payload,
        {
          params: { branchCode },
        },
      );

      toast.success(response.data.data || "Attendance marked successfully");
      return response.data;
    } catch (error: any) {
      toast.error(getErrorMessage(error));
      console.error("Error marking attendance:", error);
    }
  },

  // staff download yearly report
  getStaffYearlyReport: async (
    branchCode: string,
    academicYearStart: string,
  ): Promise<void> => {
    try {
      const response = await customFetch.get(
        `/api/v1/attendance/staff/yearly-report/${branchCode}`,
        {
          params: { academicYearStart },
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;

      const disposition = response.headers["content-disposition"];
      const fileName =
        disposition?.split("filename=")[1]?.replace(/"/g, "") ||
        `Staff_Attendance_Report_${academicYearStart}.csv`;

      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Report downloaded successfully");
    } catch (error: any) {
      toast.error("Failed to download report");
      console.error("Error downloading report:", error);
    }
  },

  // staff monthly
  getStaffMonthlyAttendance: async (
    branchCode: string,
    year: string,
    month: string,
  ): Promise<
    ApiResponse<Record<string, StaffMonthlyAttendance[]>> | undefined
  > => {
    try {
      const response = await customFetch.get<
        ApiResponse<Record<string, StaffMonthlyAttendance[]>>
      >(`/api/v1/attendance/staff/monthly/${branchCode}`, {
        params: { year, month },
      });
      return response.data;
    } catch (error: any) {
      toast.error(getErrorMessage(error));
      console.error("Error fetching monthly attendance:", error);
    }
  },

  // mark student
  markStudentAttendance: async (
    payload: MarkStudentAttendancePayload,
    branchCode: string,
  ): Promise<ApiResponse<string> | undefined> => {
    try {
      const response = await customFetch.post<ApiResponse<string>>(
        `/api/v1/attendance/student/mark`,
        payload,
        {
          params: { branchCode },
        },
      );

      toast.success(response.data.data || "Attendance marked successfully");
      return response.data;
    } catch (error: any) {
      toast.error(getErrorMessage(error));
      console.error("Error marking attendance:", error);
    }
  },

  // student download yearly report
  getStudentYearlyReport: async (
    branchCode: string,
    academicYearStart: string,
    classId: number,
  ): Promise<void> => {
    try {
      const response = await customFetch.get(
        `/api/v1/attendance/student/yearly-report/${branchCode}`,
        {
          params: { academicYearStart, classId },
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;

      const disposition = response.headers["content-disposition"];
      const fileName =
        disposition?.split("filename=")[1]?.replace(/"/g, "") ||
        `Staff_Attendance_Report_${academicYearStart}.csv`;

      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Report downloaded successfully");
    } catch (error: any) {
      toast.error("Failed to download report");
      console.error("Error downloading report:", error);
    }
  },

  // student monthly
  getStudentMonthlyAttendance: async (
    // branchCode: string,
    classId: number,
    month: string,
    year: string,
  ): Promise<
    ApiResponse<Record<string, StudentMonthlyAttendance[]>> | undefined
  > => {
    try {
      const response = await customFetch.get<
        ApiResponse<Record<string, StudentMonthlyAttendance[]>>
      >(`/api/v1/attendance/student/monthly/${classId}`, {
        params: { month, year },
      });
      return response.data;
    } catch (error: any) {
      toast.error(getErrorMessage(error));
      console.error("Error fetching monthly attendance:", error);
    }
  },

  // staff day-wise attendance
  getStaffDayWiseAttendance: async (
    branchCode: string,
    date: string,
  ): Promise<ApiResponse<StaffDayWiseAttendance[]> | undefined> => {
    try {
      const response = await customFetch.get<
        ApiResponse<StaffDayWiseAttendance[]>
      >(`/api/v1/attendance/staff/day-wise/${branchCode}`, {
        params: { date },
      });

      return response.data;
    } catch (error: any) {
      toast.error(getErrorMessage(error));
      console.error("Error fetching day-wise attendance:", error);
    }
  },

  // student day-wise attendance
  getStudentDayWiseAttendance: async (
    classId: number,
    date: string,
  ): Promise<ApiResponse<StudentDayWiseAttendance[]> | undefined> => {
    try {
      const response = await customFetch.get<
        ApiResponse<StudentDayWiseAttendance[]>
      >(`/api/v1/attendance/student/day-wise/${classId}`, {
        params: { date },
      });

      return response.data;
    } catch (error: any) {
      toast.error(getErrorMessage(error));
      console.error("Error fetching student day-wise attendance:", error);
    }
  },

  getStudentPresentStat: async (
    admissionNumber: number,
    year: string,
    month: string,
  ): Promise<
    | ApiResponse<{
        totalDays: number;
        presentDays: number;
        absentDays: number;
      }>
    | undefined
  > => {
    try {
      const response = await customFetch.get<
        ApiResponse<{
          totalDays: number;
          presentDays: number;
          absentDays: number;
        }>
      >(`/api/v1/attendance/student/present-stat/${admissionNumber}`, {
        params: { year, month },
      });

      return response.data;
    } catch (error: any) {
      toast.error(getErrorMessage(error));
      console.error("Error fetching present stats:", error);
    }
  },
};

export default attendanceApis;
